import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FileUpload, resolveMediaUrl, onMediaImgError } from "@/components/FileUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Megaphone, History, X, Pencil } from "lucide-react";
import { timeAgo } from "@/lib/timeAgo";
import { PushPrimerCardSettings } from "@/components/admin/PushPrimerCardSettings";
import { SupportAutoReplyManager } from "@/components/admin/SupportAutoReplyCard";

const ROLES = [
  { v: "all", l: "Tous les membres" },
  { v: "user", l: "Utilisateurs" },
  { v: "preacher", l: "Prédicateurs" },
  { v: "artist", l: "Artistes" },
  { v: "influencer", l: "Influenceur(e)s" },
  { v: "manager", l: "Managers" },
  { v: "moderator", l: "Modérateurs" },
];

const NOTIF_TYPES = [
  ["message", "Messages privés"], ["reaction", "Réactions"], ["comment", "Commentaires"],
  ["follow", "Nouveaux abonnés"], ["live", "Lives"], ["event", "Événements"], ["group", "Groupes"],
  ["church", "Église"], ["birthday", "Anniversaires"], ["prayer", "Prières"], ["prayer_chain", "Chaîne de prière"], ["collection", "Collections suivies"], ["ad", "Publicités (annonceur)"],
  ["ticket", "Support (tickets)"], ["verification", "Badge vérifié"],
  ["withdrawal", "Retraits & paiements"], ["moderation", "Modération"],
];

export const NotificationsPanel = ({ canManage = true, isSuper = false }) => {
  const [form, setForm] = useState({ title: "", body: "", link: "", role: "all", image: "" });
  const [sending, setSending] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [bdayBusy, setBdayBusy] = useState(false);
  const [broadcasts, setBroadcasts] = useState(null);
  const [resendForm, setResendForm] = useState(null); // null = dialog closed
  const [resending, setResending] = useState(false);

  const roleLabel = (v) => (ROLES.find((r) => r.v === (v || "all")) || {}).l || v;
  const loadBroadcasts = () =>
    api.get("/admin/notifications/broadcasts").then(({ data }) => setBroadcasts(data.broadcasts || [])).catch(() => setBroadcasts([]));

  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => setCfg(data.settings?.notifications_config || {}))
      .catch(() => setCfg({}));
    loadBroadcasts();
  }, []);

  const setType = (key, field, value) => setCfg({ ...cfg, [key]: { ...(cfg[key] || { enabled: true, mandatory: false }), [field]: value } });

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.patch("/admin/settings", { notifications_config: cfg });
      toast.success("Notifications par défaut enregistrées");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSavingCfg(false); }
  };

  const runBirthdays = async () => {
    setBdayBusy(true);
    try {
      const { data } = await api.post("/admin/notifications/birthday-run");
      toast.success(`Rappels d'anniversaire envoyés : ${data.sent} notification(s) 🎂`);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBdayBusy(false); }
  };

  const send = async () => {
    if (!form.title.trim()) { toast.info("Ajoute un titre"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", form);
      toast.success(`Notification envoyée à ${data.sent} membre(s) 🎉`);
      setForm({ title: "", body: "", link: "", role: "all", image: "" });
      loadBroadcasts();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSending(false); }
  };

  const resend = (b) => {
    setResendForm({ title: b.title || "", body: b.body || "", link: b.link || "", role: b.role || "all", image: b.image || "" });
  };
  const doResend = async () => {
    if (!resendForm?.title?.trim()) { toast.info("Ajoute un titre"); return; }
    setResending(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", resendForm);
      toast.success(`Notification renvoyée à ${data.sent} membre(s) 🎉`);
      setResendForm(null);
      loadBroadcasts();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setResending(false); }
  };

  return (
    <div className="columns-1 lg:columns-2 gap-4" data-testid="admin-notifications-panel">
      <div className="yalola-card p-5 space-y-3 mb-4 break-inside-avoid">
        <div className="flex items-center gap-2"><Megaphone size={18} style={{ color: "var(--yalola-primary)" }} /><div className="font-bold font-display text-lg">Diffuser une notification</div></div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Envoie une notification in-app à tous les membres ou à un rôle précis.</p>
        <div><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Nouvelle fonctionnalité disponible !" data-testid="notif-title" /></div>
        <div><Label>Message</Label><Textarea rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="notif-body" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Lien (optionnel)</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="/reels" data-testid="notif-link" /></div>
          <div>
            <Label>Destinataires</Label>
            <select className="w-full h-10 rounded-lg border bg-white px-3" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="notif-role">
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label>Image (optionnel)</Label>
          {form.image ? (
            <div className="relative mt-1 rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }} data-testid="notif-image-preview">
              <img src={resolveMediaUrl(form.image)} onError={onMediaImgError} alt="" className="w-full max-h-48 object-cover" />
              <button
                type="button"
                onClick={() => setForm({ ...form, image: "" })}
                className="absolute top-2 right-2 h-8 w-8 rounded-full grid place-items-center bg-black/60 text-white hover:bg-black/80"
                data-testid="notif-image-remove"
              ><X size={16} /></button>
            </div>
          ) : (
            <div className="mt-1">
              <FileUpload accept="image/*" context="notification" variant="hero" buttonLabel="Ajouter une image" testId="notif-image-upload" onUploaded={(url) => setForm((f) => ({ ...f, image: Array.isArray(url) ? url[0] : url }))} />
            </div>
          )}
          <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>Un aperçu s'affiche avant l'envoi. L'image apparaît dans la notification des membres.</p>
        </div>
        <div className="flex justify-end"><Button className="pill pill-primary" onClick={send} disabled={sending || !canManage} data-testid="notif-send"><Send size={16} className="mr-1" />{sending ? "Envoi..." : "Envoyer"}</Button></div>
      </div>

      <div className="yalola-card p-5 mb-4 break-inside-avoid" data-testid="notif-history-card">
        <div className="flex items-center gap-2 mb-1"><History size={18} style={{ color: "var(--yalola-primary)" }} /><div className="font-bold font-display text-lg">Historique des diffusions</div></div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          Les notifications que tu as diffusées. Clique sur « Renvoyer » pour recharger une notification, la modifier (destinataires, lien, image…) puis la renvoyer.
        </p>
        {broadcasts === null ? (
          <div className="text-sm py-3" style={{ color: "var(--yalola-text-muted)" }}>Chargement…</div>
        ) : broadcasts.length === 0 ? (
          <div className="text-sm py-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="notif-history-empty">Aucune diffusion pour le moment.</div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--yalola-border)" }} data-testid="notif-history-list">
            {broadcasts.map((b) => (
              <div key={b.id} className="py-3 flex items-center gap-3" data-testid={`notif-history-${b.id}`}>
                {b.image
                  ? <img src={resolveMediaUrl(b.image)} onError={onMediaImgError} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0" />
                  : <div className="h-11 w-11 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--yalola-surface-alt)" }}><Megaphone size={16} style={{ color: "var(--yalola-text-muted)" }} /></div>}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{b.title}</div>
                  <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
                    {roleLabel(b.role)} · {b.count} membre(s) · {timeAgo(b.created_at)}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => resend(b)} disabled={!canManage} data-testid={`notif-resend-${b.id}`}>
                  <Pencil size={13} className="mr-1" /> Modifier & renvoyer
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="yalola-card p-5 mb-4 break-inside-avoid" data-testid="notif-defaults-card">
        <div className="font-bold font-display text-lg mb-1">Notifications par défaut</div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          S'applique à tous les comptes. « Obligatoire » : le membre ne peut pas la désactiver. Sinon, il peut la refuser depuis ses paramètres.
        </p>
        {cfg && (
          <>
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center text-[11px] font-semibold uppercase tracking-wide pb-2" style={{ color: "var(--yalola-text-muted)" }}>
              <span>Type</span><span>Par défaut</span><span>Obligatoire</span>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--yalola-border)" }}>
              {NOTIF_TYPES.map(([key, label]) => {
                const c = cfg[key] || { enabled: true, mandatory: false };
                return (
                  <div key={key} className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center py-2.5" data-testid={`notif-default-row-${key}`}>
                    <span className="text-sm font-medium">{label}</span>
                    <Switch checked={c.enabled !== false} disabled={!!c.mandatory} onCheckedChange={(v) => setType(key, "enabled", v)} data-testid={`notif-default-enabled-${key}`} />
                    <Switch checked={!!c.mandatory} onCheckedChange={(v) => setType(key, "mandatory", v ? true : false)} data-testid={`notif-default-mandatory-${key}`} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end mt-3">
              <Button className="pill pill-primary" onClick={saveCfg} disabled={savingCfg || !canManage} data-testid="notif-defaults-save">{savingCfg ? "Enregistrement..." : "Enregistrer"}</Button>
            </div>
          </>
        )}
      </div>

      <PushPrimerCardSettings canManage={canManage} />

      {/* Lot 596 — déplacée depuis Utilisateurs : réponse automatique « Aide & support » (comptes officiels + texte + WhatsApp). */}
      <div className="mb-4 break-inside-avoid" data-testid="notif-support-autoreply-card"><SupportAutoReplyManager isSuper={isSuper} /></div>

      <div className="yalola-card p-5 mb-4 break-inside-avoid" data-testid="notif-birthday-card">
        <div className="font-bold font-display text-lg mb-1">Rappels d'anniversaire 🎂</div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          Chaque matin, les abonnés d'un membre qui fête son anniversaire reçoivent automatiquement une notification. Tu peux aussi déclencher l'envoi manuellement.
        </p>
        <Button variant="outline" className="rounded-full" onClick={runBirthdays} disabled={bdayBusy || !canManage} data-testid="notif-birthday-run">
          {bdayBusy ? "Envoi..." : "Envoyer les rappels maintenant"}
        </Button>
      </div>

      <div className="yalola-card p-5 mb-4 break-inside-avoid" data-testid="notif-digest-card">
        <div className="font-bold font-display text-lg mb-1">Résumé hebdomadaire par e-mail 📬</div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          Chaque lundi matin, les membres reçoivent un e-mail récapitulatif de leur semaine (nécessite la clé Resend dans Intégrations). Les membres peuvent le refuser depuis leurs paramètres.
        </p>
        <Button variant="outline" className="rounded-full" onClick={async () => {
          try {
            const { data } = await api.post("/admin/notifications/digest-run");
            toast.success(data.sent > 0 ? `Résumé envoyé à ${data.sent} membre(s) 📬` : "Aucun e-mail envoyé (clé Resend non configurée)");
          } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
        }} disabled={!canManage} data-testid="notif-digest-run">
          Envoyer le résumé maintenant
        </Button>
      </div>

      <div className="yalola-card p-5 mb-4 break-inside-avoid" data-testid="notif-church-digest-card">
        <div className="font-bold font-display text-lg mb-1">Résumé hebdo du journal d'église 📜</div>
        <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
          Chaque lundi, le responsable d'une page d'église reçoit (notification + e-mail) ce que ses gestionnaires délégués ont publié ou modifié pendant la semaine, regroupé par gestionnaire. Rien n'est envoyé si aucun gestionnaire n'a agi.
        </p>
        <Button variant="outline" className="rounded-full" onClick={async () => {
          try {
            const { data } = await api.post("/admin/notifications/church-digest-run");
            if (data.disabled) { toast.info("Module désactivé : Paramètres › Modules activés › « Journal d'église » / « Résumé hebdo »"); return; }
            toast.success(data.churches > 0 ? `${data.churches} page(s) d'église · ${data.notified} notification(s) · ${data.emailed} e-mail(s)` : "Aucune action de gestionnaire cette semaine : rien à envoyer");
          } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
        }} disabled={!canManage} data-testid="notif-church-digest-run">
          Envoyer le résumé maintenant
        </Button>
      </div>

      <Dialog open={!!resendForm} onOpenChange={(o) => { if (!o) setResendForm(null); }}>
        <DialogContent className="max-w-lg" data-testid="notif-resend-dialog">
          <DialogHeader><DialogTitle>Modifier & renvoyer</DialogTitle></DialogHeader>
          {resendForm && (
            <div className="space-y-3">
              <div><Label>Titre *</Label><Input value={resendForm.title} onChange={(e) => setResendForm({ ...resendForm, title: e.target.value })} data-testid="notif-resend-title" /></div>
              <div><Label>Message</Label><Textarea rows={3} value={resendForm.body} onChange={(e) => setResendForm({ ...resendForm, body: e.target.value })} data-testid="notif-resend-body" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Lien (optionnel)</Label><Input value={resendForm.link} onChange={(e) => setResendForm({ ...resendForm, link: e.target.value })} placeholder="/reels" data-testid="notif-resend-link" /></div>
                <div>
                  <Label>Destinataires</Label>
                  <select className="w-full h-10 rounded-lg border bg-white px-3" value={resendForm.role} onChange={(e) => setResendForm({ ...resendForm, role: e.target.value })} data-testid="notif-resend-role">
                    {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Image (optionnel)</Label>
                {resendForm.image ? (
                  <div className="relative mt-1 rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }} data-testid="notif-resend-image-preview">
                    <img src={resolveMediaUrl(resendForm.image)} onError={onMediaImgError} alt="" className="w-full max-h-44 object-cover" />
                    <button type="button" onClick={() => setResendForm({ ...resendForm, image: "" })} className="absolute top-2 right-2 h-8 w-8 rounded-full grid place-items-center bg-black/60 text-white hover:bg-black/80" data-testid="notif-resend-image-remove"><X size={16} /></button>
                  </div>
                ) : (
                  <div className="mt-1"><FileUpload accept="image/*" context="notification" variant="hero" buttonLabel="Ajouter une image" testId="notif-resend-image-upload" onUploaded={(url) => setResendForm((f) => ({ ...f, image: Array.isArray(url) ? url[0] : url }))} /></div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" className="rounded-full" onClick={() => setResendForm(null)}>Annuler</Button>
                <Button className="pill pill-primary" onClick={doResend} disabled={resending || !canManage} data-testid="notif-resend-submit"><Send size={15} className="mr-1" />{resending ? "Envoi..." : "Renvoyer maintenant"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
